import {ReactNode, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {MOBILE_VIEWPORT_QUERY, useMobileViewport} from 'hooks/useMobileViewport'
import {KEY_CODES} from 'definitions/constant'
import {useTranslation} from 'react-i18next'
import Logo from 'assets/images/DS-logo-white@2x.png'

interface MobilePrimaryNavigationProps {
  children: ReactNode
  onOpen: () => void
}

const focusableSelector =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Reuses the desktop links, including their route guards and prefetch hooks. */
export function MobilePrimaryNavigation({
  children,
  onOpen,
}: Readonly<MobilePrimaryNavigationProps>): JSX.Element {
  const isMobile = useMobileViewport()
  // Rendered outside a Suspense boundary: fall back to English until loaded.
  const {t} = useTranslation('common', {useSuspense: false})
  const [isOpen, setIsOpen] = useState(false)
  const panel = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const shouldRestoreFocus = useRef(true)
  const {pathname} = useLocation()

  useEffect(() => setIsOpen(false), [pathname, isMobile])

  useEffect(() => {
    const dialog = panel.current
    if (!isMobile || !isOpen || !dialog) return

    const mainLayout = document.getElementById('main-layout')
    const background = Array.from(mainLayout?.children ?? []).filter(
      element => !element.contains(dialog),
    )
    const previousInert = background.map(element =>
      element.hasAttribute('inert'),
    )
    background.forEach(element => element.setAttribute('inert', ''))

    const getControls = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter(element => element.getClientRects().length > 0)
    const frame = requestAnimationFrame(() => getControls()[0]?.focus())
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEY_CODES.ESCAPE) {
        event.preventDefault()
        setIsOpen(false)
      } else if (event.key === KEY_CODES.TAB) {
        const controls = getControls()
        const first = controls[0]
        const last = controls.at(-1)
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            !dialog.contains(document.activeElement))
        ) {
          event.preventDefault()
          last?.focus()
        } else if (
          !event.shiftKey &&
          (document.activeElement === last ||
            !dialog.contains(document.activeElement))
        ) {
          event.preventDefault()
          first?.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', handleKeyDown)
      background.forEach((element, index) => {
        if (!previousInert[index]) element.removeAttribute('inert')
      })
      if (
        shouldRestoreFocus.current &&
        window.matchMedia(MOBILE_VIEWPORT_QUERY).matches
      ) {
        trigger.current?.focus({preventScroll: true})
      }
    }
  }, [isMobile, isOpen])

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="mobile-navigation-toggle mobile-touch-target"
        aria-label={t('OPEN_NAVIGATION')}
        aria-expanded={isMobile && isOpen}
        aria-controls="primary-navigation"
        onClick={() => {
          onOpen()
          shouldRestoreFocus.current = true
          setIsOpen(true)
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M4 6h16M4 12h16M4 18h16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {isMobile && (
        <button
          className="mobile-navigation-backdrop"
          data-open={isOpen}
          aria-hidden={!isOpen}
          {...(!isOpen ? {inert: ''} : {})}
          type="button"
          aria-label={t('CLOSE_NAVIGATION')}
          tabIndex={-1}
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        ref={panel}
        id="primary-navigation"
        className="mobile-primary-navigation"
        data-open={isMobile && isOpen}
        aria-hidden={isMobile && !isOpen ? true : undefined}
        {...(isMobile && !isOpen ? {inert: ''} : {})}
        role={isMobile && isOpen ? 'dialog' : undefined}
        aria-modal={isMobile && isOpen ? true : undefined}
        aria-label={isMobile && isOpen ? t('NAVIGATION') : undefined}
        // Capture phase so every link closes the drawer, including when a route
        // guard intervenes and shows its confirmation. Keyboard users trigger it
        // too, since Enter on a link fires a click.
        onClickCapture={event => {
          if ((event.target as HTMLElement).closest('a')) {
            shouldRestoreFocus.current = false
            setIsOpen(false)
          }
        }}
      >
        <div className="mobile-primary-navigation-heading">
          <img src={Logo} alt="" className="w-10 h-10 object-contain" />
          <button
            className="mobile-touch-target"
            type="button"
            aria-label={t('CLOSE_NAVIGATION')}
            onClick={() => setIsOpen(false)}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m6 6 12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </>
  )
}
